import { useEffect, useState } from "react";

const DESKTOP_BREAKPOINT_PX = 900;

/** Диапазон «залипшей» ширины в Telegram WebView (часто 448) — только тогда подставляем screen.availWidth */
const TELEGRAM_STUCK_VIEWPORT_MIN = 400;
const TELEGRAM_STUCK_VIEWPORT_MAX = 550;

/**
 * В Telegram WebView viewport часто приходит с фиксированной шириной (например 448).
 * Подставляем screen.availWidth только если innerWidth в этом узком диапазоне,
 * иначе на мобильных получится всегда ПК-версия.
 */
function getEffectiveWidth() {
  if (typeof window === "undefined") return 0;
  const inner = window.innerWidth;
  const inTelegram = !!window.Telegram?.WebApp;
  const looksStuck =
    inTelegram &&
    inner >= TELEGRAM_STUCK_VIEWPORT_MIN &&
    inner <= TELEGRAM_STUCK_VIEWPORT_MAX;
  if (looksStuck) {
    return Math.max(inner, window.screen?.availWidth ?? 0);
  }
  return inner;
}

/** Задержка перед обновлением режима после resize — убирает прыжки при растягивании */
const RESIZE_DEBOUNCE_MS = 280;

/**
 * Возвращает true, когда ширина >= DESKTOP_BREAKPOINT_PX (ПК-режим).
 * Обновление после resize/viewportChanged с дебаунсом, чтобы не переключало туда-сюда.
 */
export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => getEffectiveWidth() >= DESKTOP_BREAKPOINT_PX);

  useEffect(() => {
    let timeoutId = null;

    function update() {
      setIsDesktop(getEffectiveWidth() >= DESKTOP_BREAKPOINT_PX);
    }

    function scheduleUpdate() {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        update();
      }, RESIZE_DEBOUNCE_MS);
    }

    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`);
    mql.addEventListener("change", update);
    window.addEventListener("resize", scheduleUpdate);
    if (window.Telegram?.WebApp?.onEvent) {
      try {
        window.Telegram.WebApp.onEvent("viewportChanged", scheduleUpdate);
      } catch {
        // ignore
      }
    }
    update();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      mql.removeEventListener("change", update);
      window.removeEventListener("resize", scheduleUpdate);
      try {
        window.Telegram?.WebApp?.offEvent?.("viewportChanged", scheduleUpdate);
      } catch {
        // ignore
      }
    };
  }, []);

  return isDesktop;
}
